import { jsPDF } from "jspdf";
import React, { useEffect, useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import { IoMdDownload } from "react-icons/io";
import { IoStar } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProfileInfo from "../../components/common/ProfileInfo";
import ProviderAvailability from "../../components/common/ProviderAvailability";
import ProviderStatistcCard from "../../components/common/ProviderStatistcCard";
import ReviewCard from "../../components/common/ReviewCard";
import ShowDocument from "../../components/common/ShowDocument";
import { fetchProviderDetails } from "../../components/commonFunctions/CommonFunctions";
import AddProviderDoc from "../../components/modal/AddProviderDoc";
import EditBankDetail from "../../components/modal/EditBankDetail";
import EditProviderAvailabilityModal from "../../components/modal/EditProviderAvailabilityModal";
import SendNotification from "../../components/modal/SendNotification";
import { getApiCaller } from "../../libs/apiCaller";
import { staticProviderContract } from "../../libs/const";
import { setLoading } from "../../redux/slices/providerSlice";

const ProviderProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const providerId = location.state?.provider?.id; // Get provider ID from navigation state
  const [sendNotificationModalOpen, setSendNotificationModalOpen] =
    useState(false);
  const [openDocModal, setOpenDocModal] = useState(false);
  const [editAvailabilityLoading, setEditAvailabilityLoading] = useState(false);
  const [editBankDetailModal, setEditBankDetailModal] = useState(false);
  const [changeDocumentData, setChangeDocumentData] = useState({});
  const [providerAvailabilityModalOpen, setProviderAvailabilityModalOpen] =
    useState(false);
  const [providerAvailabilityData, setProviderAvailabilityData] = useState({});

  const { provider, loading, allReviews } = useSelector(
    (state) => state.provider
  );

  useEffect(() => {
    if (!providerId) {
      toast.error("Provider ID is missing!");
      setLoading(false);
      return;
    }

    fetchProviderDetails(dispatch, providerId);
  }, [providerId, dispatch]);

  if (loading) {
    return (
      <div className="text-center flex items-center justify-center my-10 h-[70vh]">
        <PropagateLoader color="#284184" loading={true} size={10} />
      </div>
    );
  }

  if (!provider) {
    return (
      <p className="text-center text-lg p-4 text-red-500">
        Provider details not found.
      </p>
    );
  }

  const totalRatings = allReviews?.reduce((sum, item) => sum + item.rating, 0);
  const overallRating = totalRatings / allReviews?.length;

  const handleEditAvailability = async (selectedSlot) => {
    setEditAvailabilityLoading(true);
    const endPoint = `provider_availability_update?provider_id=${providerId}&date=${
      providerAvailabilityData.date
    }&time=${selectedSlot ?? ""}`;

    try {
      const response = await getApiCaller(endPoint);
      if (response?.data?.response_code === 200) {
        toast.success(response?.data?.response_message);
        fetchProviderDetails(dispatch, providerId);
        setProviderAvailabilityModalOpen(false);
      } else if (response?.data?.response_code === 400) {
        toast.error(
          response?.data?.response_message ??
            "Failed to Update the availability."
        );
        setProviderAvailabilityModalOpen(false);
      }
    } catch (error) {
      toast.error("Failed to fetch data.");
    }
    setEditAvailabilityLoading(false);
  };

  const handleDownloadPdf = () => {
    const doc = new jsPDF({
      orientation: "p", // Portrait mode
      unit: "mm",
      format: "a4", // A4 size
    });

    const marginLeft = 10; // Left margin
    const marginTop = 20; // Initial top margin
    const pageWidth = doc.internal.pageSize.getWidth() + 60 - 2 * marginLeft; // Available width for text
    const pageHeight = doc.internal.pageSize.getHeight() - 20; // Available height for text

    // Get formatted text
    const formattedText = staticProviderContract(
      new Date().toLocaleDateString(),
      `${provider.first_name} ${provider.last_name}`
    );

    // Wrap text to fit inside the page width
    const wrappedText = doc.splitTextToSize(formattedText, pageWidth);

    let yPosition = marginTop; // Start position for text

    // Add title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Service Provider Agreement", marginLeft, yPosition);
    yPosition += 10; // Move down for body content

    // Add body content
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);

    wrappedText.forEach((line, index) => {
      // If yPosition exceeds page height, create a new page
      if (yPosition + 10 > pageHeight) {
        doc.addPage();
        yPosition = 20; // Reset y position for new page
      }
      doc.text(line, marginLeft, yPosition);
      yPosition += 7; // Line spacing
    });

    // Save the PDF
    doc.save("contract.pdf");
  };

  return (
    <>
      <div className="px-4 md:px-10 mt-4">
        <nav className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Link
              to="/provider"
              className="text-[#284184] text-xl hover:underline"
            >
              All Providers
            </Link>
            <span className="text-[#284184]">
              <FiChevronRight className="text-2xl" />
            </span>
            <span className="text-[#284184] text-xl font-bold">
              {provider.first_name} {provider.last_name}
            </span>
          </div>
          <div className="flex flex-wrap mt-3 lg:mt-0  gap-2">
            <button
              className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
              onClick={() =>
                navigate(`/providerProfile/wallet/${providerId}`, {
                  state: {
                    providerId,
                    providerName:
                      provider?.first_name + " " + provider?.last_name,
                  },
                })
              }
            >
              Load Wallet
            </button>
            <button
              className="bg-blue-700 text-white px-4 py-2 rounded-md hover:bg-blue-800"
              onClick={() => setSendNotificationModalOpen(true)}
            >
              Send Notification
            </button>
            <button
              className="bg-blue-700 text-white px-5 py-2.5 inline-flex items-center rounded-md hover:bg-blue-800"
              onClick={() => handleDownloadPdf()}
            >
              Download Contract <IoMdDownload className="w-5 h-5 ms-2 -me-1" />
            </button>
          </div>
        </nav>

        {/* Provider Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 my-5 gap-3">
          <ProviderStatistcCard
            title={"Completed appointments"}
            value={provider?.dashboard_reports?.completed_appointment}
            link={"/completedAppointments"}
            providerId={providerId}
            providerName={provider?.first_name + " " + provider?.last_name}
            type={2}
          />
          <ProviderStatistcCard
            title={"Scheduled appointments"}
            value={provider?.dashboard_reports?.scheduled_appointment}
            link={"/scheduledAppointments"}
            providerId={providerId}
            providerName={provider?.first_name + " " + provider?.last_name}
            type={1}
          />
          <ProviderStatistcCard
            title={"Cancelled appointments"}
            value={provider?.dashboard_reports?.cancel_appointments}
            link={"/cancelledAppointments"}
            providerId={providerId}
            providerName={provider?.first_name + " " + provider?.last_name}
            type={3}
          />

          <ProviderStatistcCard
            title={"Pending Requests"}
            value={provider?.dashboard_reports?.pending_appointment}
            link={"/pendingRequest"}
            providerId={providerId}
            providerName={provider?.first_name + " " + provider?.last_name}
            type={1}
          />
          <ProviderStatistcCard
            title={"Declined appointments"}
            value={provider?.dashboard_reports?.decline_appointments}
            link={"/declinedAppointment"}
            providerId={providerId}
            providerName={provider?.first_name + " " + provider?.last_name}
            type={3}
          />
          <div className="flex justify-between items-end gap-2">
            <ProviderStatistcCard
              title={"Wallet Balance"}
              value={`$${provider?.dashboard_reports?.wallet_balance}`}
            />
            <ProviderStatistcCard
              title={"Total Earning"}
              value={`$${provider?.dashboard_reports?.total_earning}`}
              link={"/totalEarning"}
              providerId={providerId}
              providerName={provider?.first_name + " " + provider?.last_name}
            />
          </div>
        </div>

        {/* Profile Section */}
        <ProfileInfo
          provider={provider}
          listProvider={location.state?.provider}
        />

        {/* Documents Section */}
        <ShowDocument
          provider={provider}
          setOpenDocModal={setOpenDocModal}
          documents={provider?.document_list}
          documentExpireDate={provider?.doc_expiry_date}
          setChangeDocumentData={setChangeDocumentData}
          changeDocumentData={changeDocumentData}
        />

        {/* Bank Details */}
        {/* <div className="border border-1 px-4 py-3 rounded-t-2xl text-xl mt-4 bg-white">
          <div className="flex justify-between">
            <p className="text-[#284184] font-bold">Bank Detail</p>
            <button
              className="text-[#E0A256] text-base flex items-center space-x-1"
              onClick={() => setEditBankDetailModal(true)}
            >
              <CiEdit size={20} />
              <span className="underline">Edit Detail</span>
            </button>
          </div>
        </div>
        <div className="border border-t-0 rounded-b-2xl px-4 pt-4 bg-white">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="pb-2 border-b-2">
              <label className="text-sm text-gray-500">Billing Address</label>
              <div className="font-medium">
                {provider?.billing_details?.billing_street}
              </div>
            </div>
            <div className="pb-2 border-b-2">
              <label className="text-sm text-gray-500">City</label>
              <div className="font-medium">
                {provider?.billing_details?.billing_city}
              </div>
            </div>
            <div className="pb-2 border-b-2">
              <label className="text-sm text-gray-500">State</label>
              <div className="font-medium">
                {provider?.billing_details?.billing_state}
              </div>
            </div>
            <div className="pb-2 border-b-2">
              <label className="text-sm text-gray-500">Postal Code</label>
              <div className="font-medium">
                {provider?.billing_details?.billing_postal_code}
              </div>
            </div>
          </div>
        </div> */}

        {/* Review Section */}
        <div className="border border-1 px-4 py-3 rounded-t-2xl text-xl mt-4 bg-white">
          <div className="flex justify-between">
            <div className="flex items-center gap-2">
              <p className="text-[#284184] font-bold">Reviews</p>
              <p className="text-[#284184] font-bold flex items-center gap-1">
                <IoStar className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="font-medium">
                  {overallRating ? overallRating?.toFixed(1) : 0} (
                  {provider?.review_list?.length})
                </span>
              </p>
            </div>
            <button
              className="text-[#E0A256] text-base flex items-center space-x-1"
              onClick={() =>
                navigate(`/viewAllReview`, {
                  state: {
                    providerId,
                    providerName:
                      provider?.first_name + " " + provider?.last_name,
                    reviewData: allReviews,
                  },
                })
              }
            >
              <span className="underline">View All</span>
            </button>
          </div>
        </div>

        <div className="border border-t-0 rounded-b-2xl px-4 pt-4 bg-white">
          <div className="flex gap-4 overflow-x-auto shrink ">
            {allReviews?.length > 0 ? (
              allReviews?.map((review, index) => (
                <>
                  {index <= 4 && <ReviewCard key={index} reviewData={review} />}
                </>
              ))
            ) : (
              <p className="text-center w-full pb-4 text-gray-500 flex justify-center items-center">
                No reviews available
              </p>
            )}
          </div>
        </div>

        {/* Provider Availability */}
        <div className="border border-1 px-4 py-3 rounded-t-2xl text-xl mt-4 bg-white">
          <p className="text-[#284184] font-bold">Provider Availability</p>
        </div>
        <div className="border border-t-0 rounded-b-2xl p-4 bg-white">
          <ProviderAvailability
            availability={provider?.slot_availability}
            setProviderAvailabilityModalOpen={setProviderAvailabilityModalOpen}
            setProviderAvailabilityData={setProviderAvailabilityData}
          />
        </div>
      </div>

      <AddProviderDoc
        isOpen={openDocModal}
        onClose={() => setOpenDocModal(false)}
        changeDocumentData={changeDocumentData}
        setChangeDocumentData={setChangeDocumentData}
        provider={provider}
      />
      <EditBankDetail
        isOpen={editBankDetailModal}
        onClose={() => setEditBankDetailModal(false)}
      />
      <SendNotification
        isOpen={sendNotificationModalOpen}
        onClose={() => setSendNotificationModalOpen(false)}
        notificationMobileNumber={`${provider?.country_code} ${
          provider?.number || "-"
        }`}
        id={provider.id}
      />
      <EditProviderAvailabilityModal
        isOpen={providerAvailabilityModalOpen}
        onClose={() => setProviderAvailabilityModalOpen(false)}
        providerAvailabilityData={providerAvailabilityData}
        handleEditAvailability={handleEditAvailability}
        editAvailabilityLoading={editAvailabilityLoading}
      />
    </>
  );
};

export default ProviderProfile;
